import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { SectionCard } from "../../components/Common";
import { CalendarClock, Save, RotateCcw, Copy, ChevronDown, ChevronRight, X as XIcon, Plus, Grid3x3 } from "lucide-react";
import { WeeklyRosterView } from "./WeeklyRosterView";

const ROLE_LABELS = {
  doctor: "Doctors",
  nurse: "Nurses",
  receptionist: "Receptionists",
  lab_tech: "Lab techs",
  radiologist: "Imaging",
  pharmacist: "Pharmacists",
  cashier: "Cashiers",
};

function todayStr() { return new Date().toISOString().slice(0, 10); }
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export function DutyRosterCard() {
  const [date, setDate] = useState(todayStr());
  const [shiftName, setShiftName] = useState("Day");
  const [data, setData] = useState({ shifts: [], actuals: {}, roles: [] });
  const [targets, setTargets] = useState({});
  const [refDoc, setRefDoc] = useState(null);

  const attachReference = (file) => { if (file) setRefDoc(file); };

  const importRosterCsv = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) { toast.error("CSV appears empty"); return; }
      const header = lines[0].toLowerCase().split(",").map((h) => h.trim());
      const idx = (name) => header.indexOf(name);
      const iRole = idx("role"), iDate = idx("shift_date"), iShift = idx("shift_name"), iEmail = idx("staff_email");
      if (iRole < 0 || iDate < 0 || iShift < 0 || iEmail < 0) {
        toast.error("CSV needs columns: role, shift_date, shift_name, staff_email"); return;
      }
      // Pull registered staff for validation
      const { data: allStaff } = await api.get("/staff");
      const emailToUser = new Map((allStaff || []).map((u) => [u.email?.toLowerCase(), u]));
      // Group rows by (date, shift)
      const groups = {};
      const unknown = [];
      for (let li = 1; li < lines.length; li++) {
        const cells = lines[li].split(",").map((c) => c.trim());
        const role = cells[iRole], sd = cells[iDate], sn = cells[iShift], em = (cells[iEmail]||"").toLowerCase();
        if (!role || !sd || !sn || !em) continue;
        const u = emailToUser.get(em);
        if (!u || u.role !== role) { unknown.push(`${em} (${role})`); continue; }
        const key = `${sd}|${sn}`;
        groups[key] = groups[key] || { shift_date: sd, shift_name: sn, assignments: {} };
        (groups[key].assignments[role] = groups[key].assignments[role] || []).push({ user_id: u.user_id, name: u.name });
      }
      if (unknown.length) toast.warning(`${unknown.length} row(s) skipped — not registered: ${unknown.slice(0,3).join(", ")}${unknown.length>3?"…":""}`);
      const ops = Object.values(groups);
      if (!ops.length) { toast.error("Nothing valid to import"); return; }
      for (const g of ops) {
        // Auto-derive target from imported pin count so headcount cards still work
        const targets = Object.fromEntries(Object.entries(g.assignments).map(([r, list]) => [r, list.length]));
        await api.post("/roster", { ...g, targets });
      }
      toast.success(`Imported ${ops.length} shift block${ops.length===1?"":"s"} from CSV`);
      load();
    } catch (err) { toast.error("Import failed: " + (err?.message || "invalid CSV")); }
  };
  const [assignments, setAssignments] = useState({}); // {role: [{user_id, name}]}
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedRole, setExpandedRole] = useState(null);
  const [staffByRole, setStaffByRole] = useState({});
  const [weeklyMode, setWeeklyMode] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/roster?shift_date=${date}`);
      setData(data);
    } catch { toast.error("Failed to load roster"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [date]); // eslint-disable-line

  useEffect(() => {
    const shift = (data.shifts || []).find((s) => s.shift_name === shiftName);
    setTargets(shift?.targets || {});
    setAssignments(shift?.assignments || {});
    setNotes(shift?.notes || "");
  }, [data, shiftName]);

  const loadStaff = async (role) => {
    if (staffByRole[role]) return;
    try {
      const { data } = await api.get(`/staff?role=${role}`);
      setStaffByRole((prev) => ({ ...prev, [role]: data || [] }));
    } catch { setStaffByRole((prev) => ({ ...prev, [role]: [] })); }
  };

  const toggleRole = (role) => {
    if (expandedRole === role) { setExpandedRole(null); return; }
    setExpandedRole(role);
    loadStaff(role);
  };

  const addStaff = (role, userObj) => {
    setAssignments((prev) => {
      const cur = prev[role] || [];
      if (cur.some((u) => u.user_id === userObj.user_id)) return prev;
      return { ...prev, [role]: [...cur, { user_id: userObj.user_id, name: userObj.name }] };
    });
  };
  const removeStaff = (role, userId) => {
    setAssignments((prev) => ({ ...prev, [role]: (prev[role] || []).filter((u) => u.user_id !== userId) }));
  };

  const roles = data.roles || Object.keys(ROLE_LABELS);
  const actuals = data.actuals || {};

  const save = async () => {
    const clean = {};
    for (const r of roles) {
      const v = targets[r];
      if (v === "" || v === undefined || v === null) continue;
      const n = Number(v);
      if (!Number.isFinite(n) || n < 0 || n > 500) { toast.error(`${ROLE_LABELS[r] || r} must be 0–500`); return; }
      clean[r] = n;
    }
    const assignPayload = {};
    Object.entries(assignments).forEach(([r, arr]) => { assignPayload[r] = (arr || []).map((u) => u.user_id); });
    try {
      setSaving(true);
      await api.post("/roster", { shift_date: date, shift_name: shiftName, targets: clean, assignments: assignPayload, notes: notes || null });
      toast.success(`Roster saved for ${shiftName} · ${date}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save roster"); }
    finally { setSaving(false); }
  };

  const copyFrom = async (sourceDate, label) => {
    try {
      await api.post("/roster/copy", { source_date: sourceDate, target_date: date });
      toast.success(`Copied roster from ${label}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || `No roster on ${sourceDate}`); }
  };

  const otherShift = (data.shifts || []).find((s) => s.shift_name !== shiftName);

  return (
    <SectionCard
      title="Duty roster"
      testId="duty-roster-card"
      actions={
        weeklyMode ? (
          <button data-testid="roster-weekly-off" onClick={() => setWeeklyMode(false)} className="h-9 px-3 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Daily view</button>
        ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" data-testid="roster-date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-200 text-sm" />
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
            {["Day", "Night"].map((s) => (
              <button key={s} data-testid={`roster-shift-${s.toLowerCase()}`} type="button" onClick={() => setShiftName(s)} className={`h-8 px-3 rounded-md text-xs font-medium transition ${shiftName === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{s}</button>
            ))}
          </div>
          <button data-testid="roster-weekly-on" onClick={() => setWeeklyMode(true)} className="h-9 px-3 rounded-lg text-xs font-medium bg-[#1976D2] text-white hover:bg-[#1565C0] flex items-center gap-1.5"><Grid3x3 className="w-3.5 h-3.5" /> Weekly view</button>
          <button data-testid="roster-copy-yesterday" onClick={() => copyFrom(addDays(date, -1), "yesterday")} className="h-9 px-3 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy yesterday</button>
          <button data-testid="roster-copy-lastweek" onClick={() => copyFrom(addDays(date, -7), "last week")} className="h-9 px-3 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy last week</button>
          <button data-testid="roster-reset" onClick={() => load()} className="h-9 px-3 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
        )
      }
    >
      {weeklyMode ? (
        <WeeklyRosterView onExit={() => setWeeklyMode(false)} />
      ) : (
      <div className="p-5 space-y-4" data-testid="roster-body">
        <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-xs text-slate-700">
          <div className="flex items-start gap-2">
            <CalendarClock className="w-4 h-4 text-[#1565C0] mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-[#1565C0] mb-0.5">How this roster works</div>
              <ul className="space-y-0.5 list-disc list-inside">
                <li><span className="font-semibold">Target headcount</span> — the number of staff you want on this shift (editable per role)</li>
                <li><span className="font-semibold">Currently on staff</span> — how many are registered in your organisation for that role (read-only)</li>
                <li><span className="font-semibold">Pinned to this shift</span> — specific people assigned; they drive the "on duty" dashboard cards</li>
              </ul>
            </div>
            <div className="flex items-center gap-2">
              <a data-testid="roster-template-download" href="data:text/csv;charset=utf-8,role,shift_date,shift_name,staff_email%0Adoctor,2026-08-04,Day,e.kaggwa@stmarys.ths.ug%0Anurse,2026-08-04,Day,p.namuli@stmarys.ths.ug" download="ths-roster-template.csv" className="h-8 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs font-medium flex items-center gap-1">Download template</a>
              <label data-testid="roster-import-btn" className="h-8 px-3 rounded-md bg-[#1976D2] hover:bg-[#1565C0] text-white text-xs font-semibold flex items-center gap-1 cursor-pointer">
                Import roster
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => importRosterCsv(e.target.files?.[0])} />
              </label>
              <label data-testid="roster-attach-ref" className="h-8 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-xs font-medium flex items-center gap-1 cursor-pointer">
                Attach reference
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => attachReference(e.target.files?.[0])} />
              </label>
            </div>
          </div>
          {refDoc && (
            <div className="mt-2 text-[11px] flex items-center gap-2 text-slate-600" data-testid="roster-ref-attached">
              <span className="chip chip-slate">Reference file</span> {refDoc.name} — <span className="italic">not the source of truth for dashboard counts</span>
              <button onClick={() => setRefDoc(null)} className="ml-auto text-slate-400 hover:text-slate-700">Remove</button>
            </div>
          )}
        </div>
        <div className="space-y-2">
          {roles.map((r) => {
            const pinned = assignments[r] || [];
            const isOpen = expandedRole === r;
            const available = (staffByRole[r] || []).filter((s) => !pinned.some((p) => p.user_id === s.user_id));
            return (
              <div key={r} className="rounded-lg border border-slate-200" data-testid={`roster-cell-${r}`}>
                <div className="flex items-center gap-3 p-3">
                  <button
                    type="button"
                    data-testid={`roster-toggle-${r}`}
                    onClick={() => toggleRole(r)}
                    className="text-slate-500 hover:text-slate-800 flex items-center gap-1"
                  >
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <span className="text-sm font-semibold text-slate-800">{ROLE_LABELS[r] || r}</span>
                  </button>
                  <div className="flex flex-col items-start gap-0.5" title="How many staff you want on this shift">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Target</label>
                    <div className="flex items-center gap-1.5">
                      <input
                        data-testid={`roster-target-${r}`}
                        type="number" min="0" max="500"
                        value={targets[r] ?? ""}
                        onChange={(e) => setTargets((prev) => ({ ...prev, [r]: e.target.value }))}
                        placeholder=""
                        className={`w-14 h-8 px-2 rounded-md border text-sm font-semibold tabular ${(targets[r]===""||targets[r]==null)?"border-amber-300 bg-amber-50":"border-slate-200"}`}
                      />
                      {(targets[r]==="" || targets[r]==null) && (
                        <button type="button" data-testid={`set-target-${r}`} onClick={() => setTargets((prev)=>({...prev,[r]:actuals[r] ?? 1}))} className="text-[11px] text-[#1565C0] font-medium hover:underline whitespace-nowrap">Set target</button>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 tabular flex items-center gap-2 flex-wrap" title="Registered staff / pinned to this specific shift">
                    <span className="chip chip-slate text-[10px]" data-testid={`onstaff-${r}`}>{actuals[r] ?? 0} on staff</span>
                    <span className={`chip ${pinned.length>0?"chip-blue":"chip-slate"} text-[10px]`} data-testid={`pinned-${r}`}>{pinned.length} pinned to shift</span>
                  </div>
                  <div className="ml-auto flex flex-wrap gap-1">
                    {pinned.slice(0, 4).map((u) => (
                      <span key={u.user_id} className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700">{u.name}</span>
                    ))}
                    {pinned.length > 4 && <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-500">+{pinned.length - 4}</span>}
                  </div>
                </div>
                {isOpen && (
                  <div className="px-3 pb-3 border-t border-slate-100" data-testid={`roster-expand-${r}`}>
                    {pinned.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {pinned.map((u) => (
                          <span key={u.user_id} data-testid={`roster-pin-${r}-${u.user_id}`} className="text-xs px-2 py-1 rounded bg-blue-50 text-[#1565C0] flex items-center gap-1.5">
                            {u.name}
                            <button onClick={() => removeStaff(r, u.user_id)} className="hover:text-[#B71C1C]" data-testid={`roster-unpin-${r}-${u.user_id}`}><XIcon className="w-3 h-3" /></button>
                          </span>
                        ))}
                      </div>
                    )}
                    {available.length > 0 ? (
                      <div className="mt-2">
                        <div className="text-[11px] uppercase text-slate-500 mb-1">Add staff</div>
                        <div className="flex flex-wrap gap-1">
                          {available.slice(0, 20).map((s) => (
                            <button key={s.user_id} type="button" data-testid={`roster-add-${r}-${s.user_id}`} onClick={() => addStaff(r, s)} className="text-[11px] px-2 py-1 rounded bg-white border border-slate-200 text-slate-700 hover:border-[#1976D2] hover:text-[#1565C0] flex items-center gap-1"><Plus className="w-3 h-3" /> {s.name}</button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] text-slate-400">No more {ROLE_LABELS[r] || r} available to pin.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div>
          <label className="text-xs text-slate-500 font-medium">Shift notes</label>
          <input data-testid="roster-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Dr. Kaggwa on call for surgical emergencies" className="w-full h-10 mt-1 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        {otherShift && (
          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3" data-testid="roster-other-shift">
            <strong>{otherShift.shift_name}</strong> shift on {date} · {Object.entries(otherShift.targets || {}).map(([k, v]) => `${ROLE_LABELS[k] || k} ${v}`).join(" · ") || "No targets"}
            {otherShift.notes ? ` · ${otherShift.notes}` : ""}
          </div>
        )}
        <div className="flex items-center justify-end pt-1">
          <button data-testid="roster-save" onClick={save} disabled={saving || loading} className="h-9 px-4 rounded-lg bg-[#1976D2] text-white text-sm font-medium hover:bg-[#1565C0] disabled:opacity-60 flex items-center gap-1.5">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : `Save ${shiftName} · ${date}`}
          </button>
        </div>
      </div>
      )}
    </SectionCard>
  );
}
